'use client';

import { useState, useTransition } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import { TwoFASchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { twoFASettings } from '@/actions';
import { ExtendedUser } from '@/types/next-auth';
import { SettingsSectionWrapper } from './settings-section-wrapper';

export function TwoFAForm({ user }: { user: ExtendedUser }) {
  const t = useTranslations('UserSettings');
  const common = useTranslations('Common');

  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');

  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof TwoFASchema>>({
    resolver: zodResolver(TwoFASchema),
    defaultValues: {
      isTwoFactorEnabled: user.isTwoFactorEnabled || false,
    },
  });

  const onSubmit = (values: z.infer<typeof TwoFASchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      twoFASettings(values)
        .then((data) => {
          if (data.success) {
            setSuccess(data.success);
          } else {
            setError(data.error);
          }
        })
        .catch((err) => {
          setError('Something went wrong!');
          console.error(err);
        });
    });
  };

  return (
    <SettingsSectionWrapper
      title={t('2faTitle')}
      description={t('2faDesc')}
      error={error}
      success={success}
      footer={
        <div className="flex gap-3">
          <Button
            type="submit"
            form="2fa-form"
            className="flex-1"
            disabled={isPending}
          >
            {isPending ? common('saving') || 'Saving...' : common('save')}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id="2fa-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="isTwoFactorEnabled"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">{t('2faTitle')}</FormLabel>
                  <FormDescription>{t('2faDesc')}</FormDescription>
                </div>
                <FormControl>
                  <Switch
                    disabled={isPending}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SettingsSectionWrapper>
  );
}
